/**
 * Standard Login Request Form Component
 * 
 * This form allows Organization Admins to request a standard login link that bypasses SAML authentication.
 * The user enters their email address and receives a login link via email.
 * 
 * Supports PKCE parameters passed via URL query parameters:
 * - code_challenge: The PKCE code challenge
 * - code_challenge_method: The PKCE code challenge method (plain or S256)
 * 
 * @author octopaas
 * @since Jan-2026
 */
import React from 'react';
import '../../../css/login.css';
import {connect} from 'react-redux';
import UsernameField from '../formFields/UsernameField';
import FieldError from '../formFields/FieldError';
import {LocalForm} from 'react-redux-form';
import {resetForms, submitStandardLoginRequest, usernameOnChange} from '../../actions/actions';
import PropTypes from 'prop-types';
import LoginUtils from '../../utils/LoginUtils';

class StandardLoginRequestForm extends React.Component {

    componentDidMount() {
        const {resetForms} = this.props;
        resetForms();
    }

    handleSubmit(formValues) {
        const {submitStandardLoginForm} = this.props;
        // Extract PKCE parameters from URL query string using native URLSearchParams
        const queryParams = new URLSearchParams(window.location.search);
        const codeChallenge = queryParams.get('code_challenge');
        const codeChallengeMethod = queryParams.get('code_challenge_method');
        submitStandardLoginForm(formValues.username, codeChallenge, codeChallengeMethod);
    }

    render() {
        const {title, bodyMessage, emailLabel, emailPlaceholder, submitLabel,
            usernameError, usernameOnChange, usernameFieldValue} = this.props;

        let errorField = [];
        if (usernameError) {
            errorField = (<FieldError errorText={usernameError}/>);
        }

        return (
            <div key="StandardLoginRequestForm_1">
                <LocalForm role="form" model="loginForm" className="login-form" onSubmit={(formValues) => this.handleSubmit(formValues)}>
                    <h4 className="message-title">{title}</h4>
                    <div className="message standard-login-request-message">
                        {bodyMessage}
                    </div>
                    <UsernameField 
                        onChangeFunc={usernameOnChange} 
                        defaultValue={usernameFieldValue} 
                        label={emailLabel} 
                        placeholder={emailPlaceholder}
                        isError={(usernameError && usernameError.length > 0)}
                    />
                    {errorField}
                    <button className="wdc-btn wdc-btn-primary" disabled={!LoginUtils.isValidEmail(usernameFieldValue)}>
                        {submitLabel}
                    </button>
                </LocalForm>
            </div>
        );
    }
}

StandardLoginRequestForm.propTypes = {
    title: PropTypes.string,
    bodyMessage: PropTypes.string,
    emailLabel: PropTypes.string,
    emailPlaceholder: PropTypes.string,
    submitLabel: PropTypes.string,
    usernameError: PropTypes.string,
    usernameOnChange: PropTypes.func,
    usernameFieldValue: PropTypes.string,
    submitStandardLoginForm: PropTypes.func,
    resetForms: PropTypes.func
};

const mapStateToProps = (state) => {
    const challenge = state.login.challengeResponse || {};
    const prompts = challenge.prompts || {};
    const messagePrompt = prompts.message || {};
    const emailPrompt = prompts.email || {};
    return {
        title: challenge.title,
        bodyMessage: messagePrompt.value,
        emailLabel: emailPrompt.label,
        emailPlaceholder: emailPrompt.placeholder,
        submitLabel: challenge.submitLabel,
        usernameError: state.login.errorText,
        usernameFieldValue: state.forms.username
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        resetForms: () => {
            dispatch(resetForms());
        },
        usernameOnChange: (newValue) => {
            dispatch(usernameOnChange(newValue))
        },
        submitStandardLoginForm: (email, codeChallenge, codeChallengeMethod) => {
            dispatch(submitStandardLoginRequest(email, codeChallenge, codeChallengeMethod));
        }
    }
};

export default connect(mapStateToProps, mapDispatchToProps)(StandardLoginRequestForm);
